/**
 * Invoice payment utilities
 *
 * Handles payment portion building, derived state, and VAT calculations.
 * Credit notes always reduce the taxBase portion.
 */

import type { CustomerInvoice, CustomerType, PaymentPortion } from '~/api/types/customer'

export const DEFAULT_VAT_RATE = 0.21

/**
 * Build payment portions for a new invoice.
 *
 * COMPANY invoices get two portions: taxBase (insurance pays) + vat (company pays).
 * PERSON invoices get a single 'full' portion.
 * POLICE_TICKET always gets a single 'full' portion (fines have no VAT split).
 *
 * If amountWithoutVat/vatAmount are provided (from document metadata), those exact
 * values are used. Otherwise they are computed from cost and vatRate.
 */
export const buildPaymentPortions = (
  cost: number,
  customerType: CustomerType,
  invoiceType: string,
  options?: {
    vatRate?: number
    amountWithoutVat?: number
    vatAmount?: number
  }
): {
  payments: PaymentPortion[]
  amountWithoutVat?: number
  vatRate?: number
  vatAmount?: number
} => {
  // PERSON customers — single full portion (no VAT split)
  if (customerType === 'PERSON') {
    return {
      payments: [{
        type: 'full',
        label: 'Celkem',
        amount: cost,
        paid: false,
        payerType: 'person'
      }]
    }
  }

  // COMPANY non-ticket invoice — split into taxBase + vat
  const vatRate = options?.vatRate ?? DEFAULT_VAT_RATE
  let amountWithoutVat: number
  let vatAmount: number

  if (options?.amountWithoutVat != null && options?.vatAmount != null) {
    // Use exact values from document metadata
    amountWithoutVat = options.amountWithoutVat
    vatAmount = options.vatAmount
  } else if (options?.amountWithoutVat != null) {
    amountWithoutVat = options.amountWithoutVat
    vatAmount = cost - amountWithoutVat
  } else if (options?.vatAmount != null) {
    vatAmount = options.vatAmount
    amountWithoutVat = cost - vatAmount
  } else {
    // Compute from total — round base down so taxBase + vat === cost
    amountWithoutVat = Math.round(cost / (1 + vatRate))
    vatAmount = cost - amountWithoutVat
  }

  return {
    payments: [
      {
        type: 'taxBase',
        label: 'Základ daně',
        amount: amountWithoutVat,
        paid: false,
        payerType: 'insurance'
      },
      {
        type: 'vat',
        label: 'DPH',
        amount: vatAmount,
        paid: false,
        payerType: 'company'
      }
    ],
    amountWithoutVat,
    vatRate,
    vatAmount
  }
}

/**
 * Get the effective remaining amount on a single portion (amount minus credit notes and partial payments).
 */
export const portionRemainingAmount = (p: PaymentPortion): number => {
  const effective = p.amount - (p.creditNoteAmount ?? 0)
  if (p.paid) return 0
  return effective - (p.paidAmount ?? 0)
}

/**
 * Get the total remaining unpaid amount across all portions of an invoice.
 */
export const remainingAmount = (inv: CustomerInvoice): number => {
  const payments = inv.customData?.payments
  if (!payments?.length) return inv.paid ? 0 : inv.cost
  return payments.reduce((sum, p) => sum + portionRemainingAmount(p), 0)
}

/**
 * Whether all portions are paid.
 */
export const isFullyPaid = (inv: CustomerInvoice): boolean => {
  const payments = inv.customData?.payments
  if (!payments?.length) return inv.paid
  return payments.every(p => p.paid)
}

/**
 * Whether the invoice has any partial payment activity but is not fully paid.
 * True when: some portions are fully paid but not all, OR any portion has a partial paidAmount.
 */
export const isPartiallyPaid = (inv: CustomerInvoice): boolean => {
  const payments = inv.customData?.payments
  if (!payments?.length) return false
  if (payments.every(p => p.paid)) return false
  // Some portions paid, or any portion has a partial paidAmount
  return payments.some(p => p.paid) || payments.some(p => (p.paidAmount ?? 0) > 0)
}

/**
 * Whether a single portion has received a partial payment but is not fully paid.
 */
export const isPortionPartiallyPaid = (p: PaymentPortion): boolean =>
  !p.paid && (p.paidAmount ?? 0) > 0

/**
 * Ensure an invoice has a payments array. For legacy invoices created before the
 * payment portions system, builds a default 'full' portion from the invoice data.
 * Mutates the invoice in place and returns it.
 */
export const ensurePayments = (inv: CustomerInvoice): CustomerInvoice => {
  if (inv.customData?.payments?.length) return inv
  if (!inv.customData) inv.customData = {}
  inv.customData.payments = [{
    type: 'full',
    label: 'Celkem',
    amount: inv.cost || 0,
    paid: inv.paid,
    datePaid: inv.paid ? (inv.customData.datePaid as string | undefined) : undefined,
    payerType: 'person'
  }]
  return inv
}

/**
 * Sync the top-level `paid` boolean from the payments array.
 * Call this before every BE save to keep the denormalized flag in sync.
 */
export const syncPaidFlag = (inv: CustomerInvoice): boolean => {
  const allPaid = isFullyPaid(inv)
  inv.paid = allPaid
  return allPaid
}

/**
 * Get the payer label for display.
 */
export const getPayerLabel = (payerType: string): string => {
  switch (payerType) {
    case 'insurance': return 'Pojišťovna'
    case 'company': return 'Firma'
    case 'person': return 'Zákazník'
    default: return payerType
  }
}
